'use client'

import Link from 'next/link'
import { useState, useEffect, type ReactNode } from 'react'
import { isNormalUserSession } from '@/lib/auth'
import { useLandingLogin } from '@/app/landing/LandingLoginProvider'
import {
  landingCtaClick,
  type LandingCtaSurface,
} from '@/lib/ga'
import { analytics } from '@/lib/analytics'

const JOURNEY_HREF = {
  dashboard: '/dashboard',
  resume: '/dashboard?journey=resume',
  jd: '/dashboard?journey=jd',
  'system-design': '/dashboard?journey=system-design',
} as const

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const

export type LandingJourneyKind = keyof typeof JOURNEY_HREF

export function LandingJourneyCta({
  journey,
  ctaSurface,
  className,
  children,
}: {
  journey: LandingJourneyKind
  ctaSurface: LandingCtaSurface
  className: string
  children: ReactNode
}) {
  const base = JOURNEY_HREF[journey]
  const { openLoginModal } = useLandingLogin()
  const [href, setHref] = useState<string>(base)

  useEffect(() => {
    const incoming = new URLSearchParams(window.location.search)
    const utmParams = new URLSearchParams()
    UTM_KEYS.forEach((k) => {
      const v = incoming.get(k)
      if (v) utmParams.set(k, v)
    })
    if (utmParams.size > 0) {
      setHref(`${base}&${utmParams.toString()}`)
    }
  }, [base])

  return (
    <Link
      href={href}
      className={className}
      onClick={(e) => {
        landingCtaClick({ cta_surface: ctaSurface, journey })
        // Also to the product analytics service, so a funnel can distinguish "never saw
        // the CTA" from "saw it and did not click". GA above is a separate pipeline.
        analytics.track('cta_clicked', { ctaSurface, journey })
        if (isNormalUserSession()) return
        e.preventDefault()
        openLoginModal({ postLoginPath: href, loginEntry: ctaSurface })
      }}
    >
      {children}
    </Link>
  )
}
